/**
 * Exact ports of the v6 HUD formatters (src/stillf1/v6_common.py: fmt_gap, fmt_interval, fmt_laptime,
 * fmt_race_clock, fmt_local_clock, compound/team colours) with Python-compatible number semantics:
 *
 * - `pyFixed(x, nd)` = Python `'%.{nd}f' % x`: correctly rounded on the exact binary value, ties
 *   half-to-even (JS `toFixed` is also exact but breaks exact ties upwards: 0.125.toFixed(2) = "0.13",
 *   Python gives "0.12"), '-' kept for -0.0, 'nan' / 'inf', no exponent above 1e21.
 * - `pyRound(x)` = Python 3 `round(x)` (half to even), `pyFloorDiv` / `pyMod` = CPython float_divmod.
 */
import type { Manifest, RGB } from '../api.ts';

// ───────────────────────────── Python number semantics ─────────────────────────────

const F64 = new Float64Array(1);
const U32 = new Uint32Array(F64.buffer);          // little-endian host: [lo, hi]

/** |x| = m · 2^e exactly (x finite, non-zero) */
function decompose(x: number): { m: bigint; e: number } {
  F64[0] = Math.abs(x);
  const lo = U32[0] ?? 0, hi = U32[1] ?? 0;
  const exp = (hi >>> 20) & 0x7ff;
  let m = (BigInt(hi & 0xfffff) << 32n) | BigInt(lo);
  if (exp === 0) return { m, e: -1074 };
  m |= 1n << 52n;
  return { m, e: exp - 1075 };
}

function digitsWithPoint(r: bigint, nd: number): string {
  if (nd === 0) return r.toString();
  const s = r.toString().padStart(nd + 1, '0');
  return `${s.slice(0, s.length - nd)}.${s.slice(s.length - nd)}`;
}

/** Python `'%.{nd}f' % x` (nd 0…20). */
export function pyFixed(x: number, nd: number): string {
  if (Number.isNaN(x)) return 'nan';
  const neg = x < 0 || Object.is(x, -0);
  const sign = neg ? '-' : '';
  if (!Number.isFinite(x)) return `${sign}inf`;
  const a = Math.abs(x);
  if (a >= 1e21) {
    // a is an integer: exact digits from BigInt
    const { m, e } = decompose(a);
    return sign + digitsWithPoint((m << BigInt(e)) * 10n ** BigInt(nd), nd);
  }
  const y = a * 10 ** nd;
  const frac = y - Math.floor(y);
  if (Math.abs(frac - 0.5) < 1e-6 || y > 1e9) {
    // possible exact tie: decide on the exact binary value
    const { m, e } = decompose(a);
    if (a !== 0) {
      let N = m * 10n ** BigInt(nd) * 2n;         // 2 · |x| · 10^nd = N / D
      let D = 1n;
      if (e >= 0) N <<= BigInt(e);
      else D <<= BigInt(-e);
      if (N % D === 0n) {
        const twice = N / D;                         // integer 2·y
        if (twice % 2n === 1n) {                     // y = q + 0.5 exactly
          const q = (twice - 1n) / 2n;
          return sign + digitsWithPoint(q % 2n === 0n ? q : q + 1n, nd);
        }
      }
    }
  }
  return sign + a.toFixed(nd);
}

/** Python `format(x, '0{width}.{nd}f')`: zero-padded after the sign (also for nan/inf, as Python does). */
export function pyFixedPad(x: number, nd: number, width: number): string {
  const s = pyFixed(x, nd);
  if (s.length >= width) return s;
  const neg = s.startsWith('-');
  const body = neg ? s.slice(1) : s;
  return (neg ? '-' : '') + body.padStart(width - (neg ? 1 : 0), '0');
}

/** Python 3 `round(x)` (half to even). NaN/±inf pass through (Python raises). */
export function pyRound(x: number): number {
  if (!Number.isFinite(x)) return x;
  const f = Math.floor(x);
  const d = x - f;                                   // exact for |x| < 2^52
  if (d > 0.5) return f + 1;
  if (d < 0.5) return f;
  return f % 2 === 0 ? f : f + 1;
}

/** CPython float_divmod: [floordiv, mod] of x by y. */
export function pyDivmod(x: number, y: number): [number, number] {
  let mod = x % y;                                   // fmod
  let div = (x - mod) / y;
  if (mod) {
    if ((y < 0) !== (mod < 0)) {
      mod += y;
      div -= 1;
    }
  } else {
    mod = y < 0 ? -0 : 0;
  }
  let floordiv: number;
  if (div) {
    floordiv = Math.floor(div);
    if (div - floordiv > 0.5) floordiv += 1;
  } else {
    floordiv = (x / y < 0 || Object.is(x / y, -0)) ? -0 : 0;
  }
  return [floordiv, mod];
}

/** Python `x // y` for floats */
export function pyFloorDiv(x: number, y: number): number {
  return pyDivmod(x, y)[0];
}

/** Python `x % y` for floats */
export function pyFloatMod(x: number, y: number): number {
  return pyDivmod(x, y)[1];
}

/** Python `int(x)` (truncation); NaN → NaN */
export const pyInt = Math.trunc;

/** Python `str(int(q))` for an integral float (exact digits above 2^53) */
function intStr(q: number): string {
  const t = Math.trunc(q);
  return Math.abs(t) < 2 ** 53 ? String(t) : BigInt(t).toString();
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

// ───────────────────────────── v6 formatters ─────────────────────────────

/**
 * v6 fmt_gap, bit for bit (parity tests): 'LEADER', '+1.234', '+1:01.766', '+1 LAP', '+2 LAPS', ''.
 * Keeps the v6 glitch at the minute boundary: 59.9996 → '+60.000', 119.9997 → '+1:60.000'.
 */
export function fmtGapV6(gapS: number | null | undefined, lapsDown = 0, leaderText = 'LEADER', isLeader = true): string {
  if (lapsDown && Math.trunc(lapsDown) > 0) {
    const n = Math.trunc(lapsDown);
    return `+${String(n)} LAP${n > 1 ? 'S' : ''}`;
  }
  if (gapS === null || gapS === undefined || Number.isNaN(gapS)) return '';
  if (gapS <= 0) return isLeader ? leaderText : ''; // LEADER only for the leader: a non-leader at a feed gap of exactly 0 is a missing gap
  if (gapS < 60) return `+${pyFixed(gapS, 3)}`;
  if (!Number.isFinite(gapS)) return '';            // Python raises on int(inf // 60)
  const [q, r] = pyDivmod(gapS, 60);
  return `+${intStr(q)}:${pyFixedPad(r, 3, 6)}`;
}

/**
 * 'M:SS.sss' (or 'S.sss' below a minute when `short`) from the value rounded to the millisecond first
 * (Python '%.3f' rounding), so the minute boundary carries: 59.9996 → '1:00.000'. x ≥ 0, finite, < 1e12.
 */
function minSecRounded(x: number, short: boolean): string {
  const s = pyFixed(x, 3);
  const ms = Number(s.replace('.', ''));
  if (short && ms < 60000) return s;
  const m = Math.floor(ms / 60000), r = ms - m * 60000;
  return `${String(m)}:${pad2(Math.floor(r / 1000))}.${String(r % 1000).padStart(3, '0')}`;
}

/**
 * Tower gap text (web player): v6 fmt_gap with the minute-boundary glitch fixed (03 §10: the web player
 * fixes v6 cosmetic bugs). The value is rounded to the millisecond before choosing the format, so
 * 59.9996 → '+1:00.000' and 119.9997 → '+2:00.000'. Identical to fmtGapV6 everywhere else.
 */
export function fmtGap(gapS: number | null | undefined, lapsDown = 0, leaderText = 'LEADER', isLeader = true): string {
  if (lapsDown && Math.trunc(lapsDown) > 0) return fmtGapV6(gapS, lapsDown, leaderText, isLeader);
  if (gapS === null || gapS === undefined || Number.isNaN(gapS)) return '';
  if (gapS <= 0) return isLeader ? leaderText : ''; // LEADER only for the leader: a non-leader at a feed gap of exactly 0 is a missing gap
  if (!Number.isFinite(gapS)) return '';
  if (gapS >= 1e12) return fmtGapV6(gapS, 0, leaderText, isLeader);
  return `+${minSecRounded(gapS, true)}`;
}

/** v6 fmt_interval: '+0.4' ('' for NaN and for ≤ 0, i.e. the leader). */
export function fmtInterval(ivS: number | null | undefined, decimals = 1): string {
  if (ivS === null || ivS === undefined || Number.isNaN(ivS) || ivS <= 0) return '';
  return `+${pyFixed(ivS, decimals)}`;
}

/** v6 fmt_laptime, bit for bit: 81.432 → '1:21.432' (59.9996 → '0:60.000') */
export function fmtLaptimeV6(sec: number): string {
  if (!Number.isFinite(sec)) return '';
  const [q, r] = pyDivmod(sec, 60);
  return `${intStr(q)}:${pyFixedPad(r, 3, 6)}`;
}

/** lap time (web player): as fmtLaptimeV6 but rounded first, so 59.9996 → '1:00.000' (v6: '0:60.000') */
export function fmtLaptime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0 || sec >= 1e12) return fmtLaptimeV6(sec);
  return minSecRounded(sec, false);
}

/** v6 fmt_race_clock: race time → 'H:MM:SS' since lights out (truncating, never negative). */
export function fmtRaceClock(tRace: number): string {
  const t = Math.max(0, Number.isFinite(tRace) ? Math.trunc(tRace) : 0);
  return `${String(Math.floor(t / 3600))}:${pad2(Math.floor((t % 3600) / 60))}:${pad2(t % 60)}`;
}

/** Lights out as local seconds of day, as v6 hard-codes it (15:03:34.413 CEST). */
export const V6_LIGHTS_OUT_LOCAL_S = 15 * 3600 + 3 * 60 + 34.413;

/** local seconds of day of race t = 0 from an ISO time with offset, e.g. manifest.race.t0_local */
export function localSecondsOfDay(isoLocal: string): number {
  const m = /T(\d{2}):(\d{2}):(\d{2}(?:\.\d+)?)/.exec(isoLocal);
  if (!m) throw new Error(`bad local time ${isoLocal}`);
  return Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]);
}

/** v6 fmt_local_clock: race time → local wall clock 'HH:MM:SS'. */
export function fmtLocalClock(tRace: number, lightsOutLocalS = V6_LIGHTS_OUT_LOCAL_S): string {
  const t = Math.trunc(lightsOutLocalS + tRace) % 86400;          // wall clock wraps at midnight
  if (!Number.isFinite(t)) return '';
  return `${pad2(Math.floor(t / 3600))}:${pad2(Math.floor((t % 3600) / 60))}:${pad2(t % 60)}`;
}

/** Lights-out as local seconds of day for a race (manifest race.t0_local; v6 Monza fallback).
 *  Pass the result as `fmtLocalClock`'s second argument — the styles' hard-coded Monza default
 *  (and the literal ' CEST') is per race from M8. */
export function t0LocalSeconds(manifest: Manifest): number {
  const iso = (manifest.race as { t0_local?: string }).t0_local;
  if (typeof iso === 'string') {
    try {
      return localSecondsOfDay(iso);
    } catch {
      // fall through to the v6 default
    }
  }
  return V6_LIGHTS_OUT_LOCAL_S;
}

/** Timezone label of the race (manifest race.tz_abbrev, e.g. 'CEST' / 'SGT'); v6 Monza default.
 *  Styles append it after fmtLocalClock instead of a literal ' CEST'. */
export function tzAbbrev(manifest: Manifest): string {
  return (manifest.race as { tz_abbrev?: string }).tz_abbrev ?? 'CEST';
}

/** v6 speed readouts: int(round(speed)) */
export function speedInt(kmh: number): number {
  return pyRound(kmh);
}

/** v6 rpm readouts: int(rpm) */
export function rpmInt(rpm: number): number {
  return Math.trunc(rpm);
}

// ───────────────────────────── colours ─────────────────────────────

/** v6 COMPOUND_RGB */
export const COMPOUND_RGB: Readonly<Record<string, RGB>> = {
  S: [218, 41, 28], M: [255, 210, 0], H: [240, 240, 240], I: [67, 176, 42], W: [0, 103, 173], '?': [128, 128, 128],
};
export const COMPOUND_NAME: Readonly<Record<string, string>> = {
  S: 'SOFT', M: 'MEDIUM', H: 'HARD', I: 'INTER', W: 'WET', '?': '?',
};

/** timeline `cmp` value → 'S' | 'M' | 'H' | 'I' | 'W' | '?' */
export function compoundLetter(manifest: Manifest, cmp: number): string {
  return manifest.compounds.codes[cmp] ?? '?';
}

/** compound letter → colour (manifest.compounds.rgb, else the v6 table, else '?') */
export function compoundRgb(letter: string, manifest?: Manifest): RGB {
  return manifest?.compounds.rgb[letter] ?? COMPOUND_RGB[letter] ?? [128, 128, 128];
}

export function compoundName(letter: string, manifest?: Manifest): string {
  return manifest?.compounds.names[letter] ?? COMPOUND_NAME[letter] ?? '?';
}

/** team colour of a driver (code or car index) */
export function teamRgb(manifest: Manifest, codeOrIdx: string | number): RGB {
  const d = typeof codeOrIdx === 'number'
    ? manifest.drivers.find((x) => x.idx === codeOrIdx)
    : manifest.drivers.find((x) => x.code === codeOrIdx);
  if (!d) throw new Error(`unknown driver ${String(codeOrIdx)}`);
  return d.rgb;
}
